import { React, useState, useRef } from 'react';
import { Button, Tab, Tabs, Form, Accordion, Col, Row } from 'react-bootstrap';
import ApiManager from '../../ApiManager';
import { handleApiError } from '../../utils/apiErrorHandler';
import { Editor } from '@tinymce/tinymce-react';
import Sections from './Sections';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { applyReorder } from '../../utils/reorderUtils';

function SortableSectionWrapper({ section, children }) {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: section.Id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        visibility: isDragging ? 'hidden' : 'visible',
        display: 'flex',
        alignItems: 'flex-start',
    };
    return (
        <div ref={setNodeRef} style={style}>
            <button
                ref={setActivatorNodeRef}
                {...attributes}
                {...listeners}
                style={{ cursor: 'grab', border: 'none', background: 'none', padding: '10px 6px', color: '#666', fontSize: '16px', flexShrink: 0 }}
                title="Drag to reorder">
                ⠿
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        </div>
    );
}

const ProjectTabs = (props) => {
    const project = props.project;
    const setIsLoading = props.setIsLoading;
    const setIsLoadingProjectSections = props.setIsLoadingProjectSections;
    const executiveSummary = project.ExecutiveSummary;
    const sections = project.Sections;

    const [partneredWithConsultant, setPartneredWithConsultant] = useState(project.PartneredWithConsultant);
    const [compliesEnabled, setCompliesEnabled] = useState(project.CompliesEnabled);
    const [observationsEnabled, setObservationsEnabled] = useState(project.ObservationsEnabled);
    const [sectionsList, setSectionsList] = useState([...(sections || [])].filter(s => s.Enabled).sort((a, b) => a.SortOrder - b.SortOrder));
    const [activeSectionId, setActiveSectionId] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleSectionDragStart = (event) => {
        setActiveSectionId(event.active.id);
    };

    const handleSectionDragEnd = (event) => {
        setActiveSectionId(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        // Inside a project, Number is the frozen UD label captured when the section was
        // added — the server never reorders it, so neither does the optimistic update
        const result = applyReorder(sectionsList, active.id, over.id, { renumber: false });
        if (!result) return;

        const draggedSection = sectionsList.find(s => s.Id === active.id);
        const previousSections = sectionsList;
        setSectionsList(result.reordered);

        ApiManager.getInstance().updateProjectSection({
            ...draggedSection,
            SortOrder: result.targetSortOrder
        }).catch((error) => {
            setSectionsList(previousSections);
            handleApiError(error);
        });
    };

    const editorRef = useRef(null);
    const projectNameRef = useRef(project.Name);
    const clientNameRef = useRef(project.ClientName);
    const clientAddressRef = useRef(project.ClientAddress);
    const projectNumberRef = useRef(project.Number);

    const activeSection = sectionsList.find(s => s.Id === activeSectionId) ?? null;

    const handleSelect = (key) => {
        if (key === 'evaluate')
            setIsLoading(true);
    };

    const onSubmit = () => {
        const formData = new FormData();
        formData.append('Id', project.Id);
        formData.append('Name', projectNameRef.current.value);
        formData.append('ClientName', clientNameRef.current.value);
        formData.append('ExecutiveSummary', editorRef.current.getContent());
        formData.append('ClientAddress', clientAddressRef.current.value);
        formData.append('Number', projectNumberRef.current.value);
        formData.append('PartneredWithConsultant', partneredWithConsultant);
        formData.append('CompliesEnabled', compliesEnabled);
        formData.append('ObservationsEnabled', observationsEnabled);

        ApiManager.getInstance().updateProject(formData).then(() => {
            setIsLoading(true);
        }).catch(handleApiError);
    }

    return (
        <>
            <Tabs defaultActiveKey="evaluate" onSelect={(key) => handleSelect(key)}>
                <Tab eventKey="info" title="Project Admin" className="p-3">
                    <Form>
                        <Row className="mb-3">
                            <Form.Group as={Col}>
                                <Form.Label>Project Name</Form.Label>
                                <Form.Control as="textarea" autoFocus rows={2} defaultValue={project.Name} ref={projectNameRef} />
                            </Form.Group>
                            <Form.Group as={Col}>
                                <Form.Label>Project Number</Form.Label>
                                <Form.Control as="textarea" autoFocus rows={2} defaultValue={project.Number} ref={projectNumberRef} />
                            </Form.Group>
                        </Row>

                        <Row className="mb-3">
                            <Form.Group as={Col}>
                                <Form.Label>Client Name</Form.Label>
                                <Form.Control as="textarea" autoFocus rows={2} defaultValue={project.ClientName} ref={clientNameRef} />
                            </Form.Group>
                            <Form.Group as={Col}>
                                <Form.Label>Client Address</Form.Label>
                                <Form.Control as="textarea" autoFocus rows={2} defaultValue={project.ClientAddress} ref={clientAddressRef} />
                            </Form.Group>
                        </Row>

                        <Row className="mb-3">
                            <Form.Group as={Col} style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                                <Form.Check
                                    className="mb-4"
                                    type="switch"
                                    label='Partnered with Consultant'
                                    id='partneredCheckbox'
                                    checked={partneredWithConsultant}
                                    onChange={(e) => setPartneredWithConsultant(e.target.checked)} />
                            </Form.Group>
                            <Form.Group as={Col} style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                                <Form.Check
                                    className="mb-4"
                                    type="switch"
                                    label='Complies Enabled'
                                    id='cCheckbox'
                                    checked={compliesEnabled}
                                    onChange={(e) => setCompliesEnabled(e.target.checked)} />
                            </Form.Group>
                            <Form.Group as={Col} style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                                <Form.Check
                                    className="mb-4"
                                    type="switch"
                                    label='Observations Enabled'
                                    id='oCheckbox'
                                    checked={observationsEnabled}
                                    onChange={(e) => setObservationsEnabled(e.target.checked)} />
                            </Form.Group>
                        </Row>

                        <Row className="mb-3">
                            <Form.Group as={Col}>
                                <Form.Label>Executive Summary</Form.Label>
                                <Editor
                                    apiKey={process.env.REACT_APP_TINYAPI}
                                    onInit={(evt, editor) => editorRef.current = editor}
                                    initialValue={executiveSummary}
                                    init={{
                                        height: 550,
                                        menubar: true,
                                        plugins: 'lists',
                                        toolbar: 'undo redo | formatselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | help'
                                    }}
                                />
                            </Form.Group>
                        </Row>

                        <div className="text-end mb-3">
                            <Button variant="outline-success" onClick={() => onSubmit()}> Save </Button>
                        </div>
                    </Form>
                </Tab>
                <Tab eventKey="evaluate" title="Evaluation">
                    <Accordion>
                        {sectionsList.length > 0 ?
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleSectionDragStart} onDragEnd={handleSectionDragEnd}>
                                <SortableContext items={sectionsList.map(s => s.Id)} strategy={verticalListSortingStrategy}>
                                    {sectionsList.map((section) =>
                                        <SortableSectionWrapper key={section.Id} section={section}>
                                            <Sections
                                                setIsLoading={setIsLoading}
                                                setIsLoadingProjectSections={setIsLoadingProjectSections}
                                                editingProject={true}
                                                canEditSections={true}
                                                section={section}
                                                project={project}
                                            />
                                        </SortableSectionWrapper>
                                    )}
                                </SortableContext>
                                <DragOverlay dropAnimation={null}>
                                    {activeSection && (
                                        <div style={{ background: 'white', border: '1px solid #dee2e6', borderRadius: '4px', padding: '10px 16px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', cursor: 'grabbing' }}>
                                            {activeSection.Number} | {activeSection.Name}
                                        </div>
                                    )}
                                </DragOverlay>
                            </DndContext> :
                            <div className="d-flex justify-content-center py-4">
                                <h5 className="text-muted">No Sections Assigned</h5>
                            </div>
                        }
                    </Accordion>
                </Tab>
            </Tabs>
        </>
    )
};

export default ProjectTabs;